import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Upload, Check, X, FileText } from "lucide-react";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_UPLOAD_MB = 20;

// Career-search vocabulary — kept tight so the form finishes in <3 min.
const INDUSTRIES = [
  "Trades & construction", "Tech / IT", "Health & care", "Hospitality & tourism",
  "Retail / customer service", "Corporate / finance", "Creative / marketing",
  "Education", "Engineering", "Logistics / transport", "Other",
];
const TARGET_CITIES = [
  "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide",
  "Gold Coast", "Canberra", "Hobart", "Anywhere in AUS", "Not sure yet",
];
const SENIORITY = ["Entry / apprentice", "Mid-level", "Senior", "Lead / Exec"];
const YEARS_EXPERIENCE = ["0–2 years", "3–5 years", "6–10 years", "10+ years"];
const TIMELINES = ["Within 3 months", "3–6 months", "6–12 months", "Just researching"];
const WORK_RIGHTS = ["NZ passport (SCV)", "AU citizen", "AU permanent resident", "Visa needed"];
const SITUATIONS = [
  "Actively looking",
  "Employed — open to move",
  "Recently graduated",
  "Between roles",
  "Self-employed",
];

const roman = (n) => ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x", "xi"][n - 1] || String(n);

function RN({ n }) {
  return <span className="italic text-[var(--ocean)] font-serif text-sm mr-2 min-w-[1.5rem]">{roman(n)}.</span>;
}

function Pill({ active, children, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "px-5 py-2 rounded-full border text-sm font-medium transition-colors",
        active
          ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
          : "bg-white text-[var(--ink)] border-[#D6DCE3] hover:border-[var(--ink)]",
      )}
    >
      {children}
    </button>
  );
}

function TextField({ label, n, value, onChange, type = "text", testid, placeholder = "", hint = "" }) {
  return (
    <div>
      <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-2">
        <RN n={n} />
        <span>{label}</span>
      </label>
      {hint && <p className="text-xs text-[var(--ocean)] -mt-1 mb-2 ml-6">{hint}</p>}
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-[#D6DCE3] rounded-xl px-4 py-3 text-[var(--ink)] placeholder:text-[#B8C3CE] focus:outline-none focus:border-[var(--ink)] transition-colors"
      />
    </div>
  );
}

// ── Upload primitive ───────────────────────────────────────────────
// Single file picker with instant client-side base64 encode.
function FileSlot({ label, hint, file, onFile, onClear, testid, accept }) {
  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`${f.name} is too large. Keep uploads under ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    const b64 = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || "").split(",", 2)[1] || "");
      fr.onerror = reject;
      fr.readAsDataURL(f);
    });
    onFile({ name: f.name, content_type: f.type || "application/octet-stream", data_b64: b64, _size: f.size });
  };
  return (
    <div className="border border-[#D6DCE3] rounded-xl px-4 py-3 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--ink)] leading-snug">{label}</p>
          {hint && <p className="text-xs text-[var(--ocean)] mt-0.5">{hint}</p>}
          {file && (
            <p className="mt-2 text-xs text-[var(--ink)] inline-flex items-center gap-1.5">
              <FileText size={12} /> {file.name}
              <span className="text-[var(--ocean)]">· {Math.round(file._size / 1024)} KB</span>
            </p>
          )}
        </div>
        {file ? (
          <button type="button" onClick={onClear}
                  className="text-xs text-[var(--ocean)] hover:text-[var(--ink)] inline-flex items-center gap-1 shrink-0"
                  data-testid={`${testid}-clear`}>
            <X size={12} /> Replace
          </button>
        ) : (
          <label className="cursor-pointer text-xs font-semibold text-[var(--ink)] bg-[#C6D21F] hover:bg-[#d3df34] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 shrink-0"
                 data-testid={`${testid}-picker`}>
            <Upload size={12} /> Upload
            <input type="file" className="hidden" onChange={onChange} accept={accept} />
          </label>
        )}
      </div>
    </div>
  );
}

// ── Completion checklist gate ──────────────────────────────────────
function ChecklistRow({ done, label, testid }) {
  return (
    <li className="flex items-center gap-2 text-sm" data-testid={testid}>
      <span className={cn(
        "h-5 w-5 rounded-full inline-flex items-center justify-center shrink-0",
        done ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[#D6DCE3] text-[var(--paper)]",
      )}>
        {done ? <Check size={12} /> : <span className="h-1.5 w-1.5 rounded-full bg-[var(--paper)]/70" />}
      </span>
      <span className={cn(done ? "text-[var(--ink)]" : "text-[var(--ocean)]")}>{label}</span>
    </li>
  );
}

export default function RoleSearchIntake() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    industry: "",
    target_city: "",
    seniority: "",
    years_experience: "",
    move_timeline: "",
    work_rights: "",
    current_situation: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [photoIdFile, setPhotoIdFile] = useState(null);
  const [ref1, setRef1] = useState(null);
  const [ref2, setRef2] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill from Smart Redirect Funnel (Careers popup hand-off).
  useEffect(() => {
    let prefill = { email: "", first_name: "" };
    try {
      const p = new URLSearchParams(window.location.search);
      const pe = (p.get("email") || "").trim().toLowerCase();
      const pf = (p.get("first_name") || "").trim();
      if (pe) prefill.email = pe;
      if (pf) prefill.first_name = pf;
      if (!prefill.email && !prefill.first_name) {
        const stashed = sessionStorage.getItem("tdc_prefill");
        if (stashed) {
          const s = JSON.parse(stashed);
          prefill.email = (s.email || "").trim().toLowerCase();
          prefill.first_name = (s.first_name || "").trim();
        }
      }
    } catch (_) {}
    if (prefill.email || prefill.first_name) {
      setForm((f) => ({ ...f, ...prefill }));
      try { sessionStorage.removeItem("tdc_prefill"); } catch (_) {}
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Completion checklist rules — MUST match server-side validation.
  const check = {
    identity:  !!(form.first_name.trim() && form.last_name.trim() && form.email.trim()),
    industry:  !!form.industry,
    experience: !!form.years_experience,
    fit:       !!(form.target_city && form.seniority && form.move_timeline && form.work_rights && form.current_situation),
    cv:        !!cvFile,
    photo_id:  !!photoIdFile,
    refs:      !!(ref1 && ref2),
  };
  const canSubmit = Object.values(check).every(Boolean) && !submitting;

  async function submit(e) {
    e?.preventDefault();
    if (!canSubmit) {
      toast.error("Complete every checklist item before joining the talent pool.");
      return;
    }
    setSubmitting(true);
    try {
      const stripSize = (f) => f && { name: f.name, content_type: f.content_type, data_b64: f.data_b64 };
      await axios.post(`${API}/leads/careers-role-search`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone_number: form.phone_number.trim(),
        industry: form.industry,
        target_city: form.target_city,
        seniority: form.seniority,
        years_experience: form.years_experience,
        move_timeline: form.move_timeline,
        work_rights: form.work_rights,
        current_situation: form.current_situation,
        cv_file: stripSize(cvFile),
        photo_id_file: stripSize(photoIdFile),
        reference_files: [stripSize(ref1), stripSize(ref2)].filter(Boolean),
      }, { timeout: 120_000 });
      setSubmitted(true);
      toast.success("You're in the talent pool — Laura will match you against live roles.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went sideways. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries and try again.");
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid="role-search-success">
          <h1 className="font-display text-4xl font-bold text-[var(--ink)] mb-4">You're in the door.</h1>
          <p className="text-lg text-[var(--ocean)] mb-6">
            Your role brief is with the Careers team. We'll come back to you with a short-list of live roles
            that match your ask — usually within 2 business days.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] px-6 py-3 rounded-full font-semibold hover:opacity-90"
          >
            Back to homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <a href="/" className="font-display font-bold text-[var(--ink)] text-lg">The Ditch Careers</a>
        <a href="/" className="text-xs text-[var(--ocean)] hover:text-[var(--ink)]">← Back to homepage</a>
      </header>
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <p className="text-xs uppercase tracking-widest text-[var(--ocean)] mb-2">05 — Your role search</p>
        <h1 className="font-display text-5xl font-bold text-[var(--ink)] leading-tight">
          Tell us about <em className="not-italic text-[var(--ocean)]">the roles</em> you're after.
        </h1>
        <p className="text-lg text-[var(--ocean)] mt-3 mb-8">Takes 3 minutes. Then we start matching.</p>

        {/* Completion checklist — always visible so the candidate knows exactly what's left. */}
        <aside className="mb-10 bg-white border border-[#D6DCE3] rounded-2xl p-5" data-testid="completion-checklist">
          <p className="text-xs uppercase tracking-widest text-[var(--ocean)] mb-3">Completion checklist</p>
          <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
            <ChecklistRow done={check.identity}   testid="check-identity"   label="Your name & email" />
            <ChecklistRow done={check.industry}   testid="check-industry"   label="Primary industry" />
            <ChecklistRow done={check.experience} testid="check-experience" label="Years of experience" />
            <ChecklistRow done={check.fit}        testid="check-fit"        label="City, seniority, timeline & rights" />
            <ChecklistRow done={check.cv}         testid="check-cv"         label="CV uploaded" />
            <ChecklistRow done={check.photo_id}   testid="check-photo-id"   label="Photo ID uploaded" />
            <ChecklistRow done={check.refs}       testid="check-refs"       label="2 References uploaded" />
          </ul>
        </aside>

        <form onSubmit={submit} className="space-y-8" data-testid="role-search-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <TextField label="First name" n={1} value={form.first_name}
              onChange={(v) => update("first_name", v)} testid="role-first-name" />
            <TextField label="Last name" n={2} value={form.last_name}
              onChange={(v) => update("last_name", v)} testid="role-last-name" />
          </div>
          <TextField label="Email address" n={3} type="email" value={form.email}
            onChange={(v) => update("email", v)} testid="role-email" />
          <TextField label="Phone number" n={4} value={form.phone_number}
            onChange={(v) => update("phone_number", v)} testid="role-phone"
            hint="NZ or AU number" placeholder="+64 21 …" />

          {[
            { n: 5, label: "Which industry?", key: "industry", options: INDUSTRIES, group: "industry", required: true },
            { n: 6, label: "Years of experience?", key: "years_experience", options: YEARS_EXPERIENCE, group: "years", required: true },
            { n: 7, label: "Target city in AUS?", key: "target_city", options: TARGET_CITIES, group: "city" },
            { n: 8, label: "Where are you in your career?", key: "seniority", options: SENIORITY, group: "seniority" },
            { n: 9, label: "When are you planning to move?", key: "move_timeline", options: TIMELINES, group: "timeline" },
            { n: 10, label: "Work rights in AUS?", key: "work_rights", options: WORK_RIGHTS, group: "work-rights" },
            { n: 11, label: "What best describes your current situation?", key: "current_situation", options: SITUATIONS, group: "situation" },
          ].map(({ n, label, key, options, group, required }) => (
            <div key={key}>
              <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-3">
                <RN n={n} /><span>{label}{required && <span className="text-red-500 ml-1">*</span>}</span>
              </label>
              <div className="flex flex-wrap gap-2" data-testid={`role-${group}-group`}>
                {options.map((o) => (
                  <Pill
                    key={o}
                    active={form[key] === o}
                    onClick={() => update(key, o)}
                    testid={`role-${group}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {o}
                  </Pill>
                ))}
              </div>
            </div>
          ))}

          {/* Profile Gate uploads */}
          <div>
            <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-3">
              <RN n={12} /><span>Upload your CV, photo ID and two references<span className="text-red-500 ml-1">*</span></span>
            </label>
            <p className="text-xs text-[var(--ocean)] ml-6 mb-3">PDF, PNG or JPG. Max {MAX_UPLOAD_MB} MB per file. Nothing gets sent to employers until you approve it.</p>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="role-uploads">
              <FileSlot label="Your CV"          hint="Latest version — we'll help brand it later." accept=".pdf,.doc,.docx,.rtf" file={cvFile}      onFile={setCvFile}      onClear={() => setCvFile(null)}      testid="upload-cv" />
              <FileSlot label="Photo ID"         hint="Passport, driver's licence or similar."      accept="image/*,.pdf"          file={photoIdFile} onFile={setPhotoIdFile} onClear={() => setPhotoIdFile(null)} testid="upload-photo-id" />
              <FileSlot label="Reference #1"     hint="Written reference or LinkedIn recommendation." accept=".pdf,image/*,.doc,.docx" file={ref1}       onFile={setRef1}        onClear={() => setRef1(null)}        testid="upload-ref-1" />
              <FileSlot label="Reference #2"     hint="Second referee from a different role."       accept=".pdf,image/*,.doc,.docx" file={ref2}       onFile={setRef2}        onClear={() => setRef2(null)}        testid="upload-ref-2" />
            </div>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="role-submit"
            className={cn(
              "inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors",
              canSubmit
                ? "bg-[var(--ink)] text-[var(--paper)] hover:opacity-90"
                : "bg-[#D6DCE3] text-[var(--ocean)] cursor-not-allowed",
            )}
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
              : <>Join Talent Pool <ArrowRight size={16} /></>}
          </button>
          {!canSubmit && !submitting && (
            <p className="text-xs text-[var(--ocean)]" data-testid="role-submit-hint">
              Complete every checklist item above to unlock the button.
            </p>
          )}
        </form>
      </main>
    </div>
  );
}
